import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Activity,
  Search,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Minus,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Info,
  ArrowUpRight,
} from 'lucide-react';
import { fetchGmp, selectGmpSectionRows } from '../../store/slices/gmpSlice';
import { buildStatusTabs, filterGmpRows, trackedSummary } from '../../utils/gmpSection';

const PAGE_SIZE = 10;
const COLUMN_COUNT = 11;

const statusBadgeClass = (statusKey) => {
  if (/open|live|active/.test(statusKey)) {
    return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  }
  if (/upcoming|forthcoming/.test(statusKey)) {
    return 'bg-sky-50 text-sky-700 border-sky-200';
  }
  if (/allot/.test(statusKey)) {
    return 'bg-amber-50 text-amber-700 border-amber-200';
  }
  if (/list/.test(statusKey)) {
    return 'bg-violet-50 text-violet-700 border-violet-200';
  }
  return 'bg-slate-100 text-slate-600 border-slate-200';
};

const TREND_STYLES = {
  up: { className: 'text-emerald-600', Icon: TrendingUp },
  down: { className: 'text-rose-600', Icon: TrendingDown },
  flat: { className: 'text-slate-600', Icon: Minus },
};

const formatRefreshedAt = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatSource = (value) => {
  if (!value) return null;
  try {
    return new URL(value).hostname.replace(/^www\./, '');
  } catch {
    return value;
  }
};

const Placeholder = ({ children = '—' }) => (
  <span className="text-slate-400">{children}</span>
);

export const GmpSection = () => {
  const dispatch = useDispatch();
  const items = useSelector(selectGmpSectionRows);
  const { refreshedAt, source, status, error } = useSelector((state) => state.gmp);

  const [searchQuery, setSearchQuery] = useState('');
  const [boardFilter, setBoardFilter] = useState('ALL'); // 'ALL' | 'MAINBOARD' | 'SME'
  const [statusFilter, setStatusFilter] = useState('all');
  const [gmpOnly, setGmpOnly] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Keep showing loaded rows while a refresh is in flight or after it fails
  const hasItems = items.length > 0;
  const isLoading = !hasItems && (status === 'idle' || status === 'loading');
  const isFailed = !hasItems && status === 'failed';

  useEffect(() => {
    dispatch(fetchGmp({ maxAgeMs: 60000 }));
  }, [dispatch]);

  // Back to page 1 whenever the visible set changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, boardFilter, statusFilter, gmpOnly]);

  // Counts follow the board, search and GMP-only filters, so each chip shows
  // exactly how many rows clicking it would list
  const { allCount, tabs: statusTabs } = useMemo(
    () => buildStatusTabs(items, { query: searchQuery, board: boardFilter, gmpOnly }),
    [items, searchQuery, boardFilter, gmpOnly]
  );

  const filteredItems = useMemo(
    () => filterGmpRows(items, { query: searchQuery, board: boardFilter, status: statusFilter, gmpOnly }),
    [items, searchQuery, boardFilter, statusFilter, gmpOnly]
  );

  const tracked = useMemo(() => trackedSummary(items, boardFilter), [items, boardFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / PAGE_SIZE));
  const pageItems = filteredItems.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleRefresh = () => {
    dispatch(fetchGmp());
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
      setCurrentPage(newPage);
      const sectionElem = document.getElementById('gmp');
      if (sectionElem) {
        sectionElem.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setBoardFilter('ALL');
    setStatusFilter('all');
    setGmpOnly(false);
  };

  const pillClass = (active) =>
    `px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
      active ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-600 hover:text-slate-900'
    }`;

  const chipClass = (active) =>
    `inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border transition-all ${
      active
        ? 'bg-[#008080] text-white border-[#008080]'
        : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:text-slate-900'
    }`;

  const renderPaginationNumbers = () => {
    const buttons = [];
    const maxButtons = 5;
    let startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, startPage + maxButtons - 1);

    if (endPage - startPage < maxButtons - 1) {
      startPage = Math.max(1, endPage - maxButtons + 1);
    }

    for (let p = startPage; p <= endPage; p++) {
      buttons.push(
        <button
          key={p}
          onClick={() => handlePageChange(p)}
          className={`w-8 h-8 rounded-lg text-xs font-semibold transition-all ${
            currentPage === p
              ? 'bg-[#008080] text-white shadow-xs'
              : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
          }`}
        >
          {p}
        </button>
      );
    }
    return buttons;
  };

  const renderGmpCell = (row) => {
    if (!row.gmpDisplay) {
      return (
        <div className="leading-tight">
          <Placeholder />
          <div className="text-[11px] text-slate-400 whitespace-nowrap">Not available yet</div>
        </div>
      );
    }
    const trend = TREND_STYLES[row.trend];
    const TrendIcon = trend?.Icon;
    return (
      <div className="leading-tight">
        <div className={`flex items-center gap-1 font-extrabold ${trend?.className || 'text-slate-800'}`}>
          {TrendIcon && <TrendIcon className="w-3.5 h-3.5 flex-shrink-0" />}
          <span>{row.gmpDisplay}</span>
        </div>
        {row.gmpPercentDisplay && (
          <div className={`text-[11px] font-semibold ${trend?.className || 'text-slate-500'}`}>
            {row.gmpPercentDisplay}
          </div>
        )}
        {row.dayRangeDisplay && (
          <div className="text-[11px] text-slate-400 whitespace-nowrap" title="Day low / Day high">
            L/H {row.dayRangeDisplay}
          </div>
        )}
      </div>
    );
  };

  const refreshedLabel = formatRefreshedAt(refreshedAt);
  const sourceLabel = formatSource(source);

  return (
    <section
      id="gmp"
      className="py-12 sm:py-16 bg-slate-50/50 border-t border-slate-200/80"
    >
      <div className="w-full max-w-[1680px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-28">

        {/* Section Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-5">
          <div>
            <div className="flex flex-wrap items-center gap-2.5">
              <span className="w-2.5 h-2.5 rounded-full bg-[#008080] animate-pulse" />
              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                IPO Grey Market Premium (GMP)
              </h2>
              {items.length > 0 && (
                <span className="hidden sm:inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-teal-50 text-[#008080] border border-teal-200">
                  {tracked.label}
                </span>
              )}
            </div>
            <p className="text-slate-500 text-xs sm:text-sm mt-1 max-w-2xl">
              Live grey-market premium for upcoming and open issues, with estimated listing price and key dates. GMP often isn't quoted until an issue is close to opening.
            </p>
            <Link
              to="/ipo-gmp"
              className="inline-flex items-center gap-1 mt-2 text-xs font-bold text-[#008080] hover:text-[#006666] hover:underline"
            >
              View Full GMP Board
              <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          {/* Board Filter, Search & Refresh */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <div className="inline-flex p-1 bg-slate-100 rounded-xl border border-slate-200/80 self-start sm:self-auto">
              <button onClick={() => setBoardFilter('ALL')} className={pillClass(boardFilter === 'ALL')}>
                All Boards
              </button>
              <button onClick={() => setBoardFilter('MAINBOARD')} className={pillClass(boardFilter === 'MAINBOARD')}>
                Mainboard
              </button>
              <button onClick={() => setBoardFilter('SME')} className={pillClass(boardFilter === 'SME')}>
                SME
              </button>
            </div>

            <div className="flex items-center gap-2">
              <div className="relative flex-1 sm:min-w-[220px]">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search company..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-7 py-1.5 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 placeholder-slate-400 focus:outline-hidden focus:border-[#008080] focus:ring-1 focus:ring-[#008080] transition-all"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-slate-400 hover:text-slate-600"
                    aria-label="Clear search"
                  >
                    ✕
                  </button>
                )}
              </div>

              <button
                onClick={handleRefresh}
                disabled={status === 'loading'}
                className="inline-flex items-center justify-center w-8 h-8 rounded-xl border border-slate-200 bg-white text-slate-600 hover:text-[#008080] hover:border-teal-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-2xs flex-shrink-0"
                aria-label="Refresh GMP data"
                title="Refresh GMP data"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${status === 'loading' ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Status Tabs & GMP-only Toggle */}
        {statusTabs.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <button onClick={() => setStatusFilter('all')} className={chipClass(statusFilter === 'all')}>
              All <span className="opacity-70">{allCount}</span>
            </button>
            {statusTabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setStatusFilter(tab.key)}
                className={chipClass(statusFilter === tab.key)}
              >
                {tab.label} <span className="opacity-70">{tab.count}</span>
              </button>
            ))}
            <label className="ml-auto inline-flex items-center gap-2 text-xs font-semibold text-slate-600 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={gmpOnly}
                onChange={(e) => setGmpOnly(e.target.checked)}
                className="w-3.5 h-3.5 rounded border-slate-300 accent-[#008080]"
              />
              Only IPOs with live GMP
            </label>
          </div>
        )}

        {/* Data Table Card Container */}
        <div className="bg-white rounded-2xl border border-slate-200/90 shadow-xs overflow-hidden">
          <div className="overflow-x-auto scrollbar-none">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/90 border-b border-slate-200/80 text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                  <th className="py-3.5 px-4 sm:px-6 min-w-[220px]">Company</th>
                  <th className="py-3.5 px-4 text-center">Status</th>
                  <th className="py-3.5 px-4 min-w-[110px]">GMP</th>
                  <th className="py-3.5 px-4 whitespace-nowrap">Est. Listing</th>
                  <th className="py-3.5 px-4 whitespace-nowrap">Price</th>
                  <th className="py-3.5 px-4 whitespace-nowrap hidden md:table-cell">Issue Size</th>
                  <th className="py-3.5 px-4 whitespace-nowrap hidden lg:table-cell">Lot Size</th>
                  <th className="py-3.5 px-4 whitespace-nowrap hidden lg:table-cell">Subscription</th>
                  <th className="py-3.5 px-4 whitespace-nowrap">Open – Close</th>
                  <th className="py-3.5 px-4 whitespace-nowrap hidden xl:table-cell">Allotment</th>
                  <th className="py-3.5 px-4 sm:px-6 whitespace-nowrap">Listing</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100 text-xs sm:text-sm">
                {isLoading ? (
                  /* Loading State Skeleton */
                  Array.from({ length: 6 }).map((_, idx) => (
                    <tr key={`gmp-skel-${idx}`} className="animate-pulse">
                      <td className="py-4 px-4 sm:px-6">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-slate-200" />
                          <div className="space-y-1.5 flex-1">
                            <div className="w-40 h-4 bg-slate-200 rounded" />
                            <div className="w-20 h-3 bg-slate-100 rounded" />
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4"><div className="w-16 h-5 bg-slate-200 rounded-full mx-auto" /></td>
                      <td className="py-4 px-4"><div className="w-14 h-4 bg-slate-200 rounded" /></td>
                      <td className="py-4 px-4"><div className="w-14 h-4 bg-slate-200 rounded" /></td>
                      <td className="py-4 px-4"><div className="w-12 h-4 bg-slate-200 rounded" /></td>
                      <td className="py-4 px-4 hidden md:table-cell"><div className="w-16 h-4 bg-slate-200 rounded" /></td>
                      <td className="py-4 px-4 hidden lg:table-cell"><div className="w-10 h-4 bg-slate-200 rounded" /></td>
                      <td className="py-4 px-4 hidden lg:table-cell"><div className="w-10 h-4 bg-slate-200 rounded" /></td>
                      <td className="py-4 px-4"><div className="w-24 h-4 bg-slate-200 rounded" /></td>
                      <td className="py-4 px-4 hidden xl:table-cell"><div className="w-12 h-4 bg-slate-200 rounded" /></td>
                      <td className="py-4 px-4 sm:px-6"><div className="w-12 h-4 bg-slate-200 rounded" /></td>
                    </tr>
                  ))
                ) : isFailed ? (
                  /* Error State */
                  <tr>
                    <td colSpan={COLUMN_COUNT} className="py-12 text-center">
                      <div className="max-w-md mx-auto space-y-3 px-4">
                        <div className="w-12 h-12 rounded-full bg-rose-50 text-rose-600 flex items-center justify-center mx-auto">
                          <RefreshCw className="w-5 h-5" />
                        </div>
                        <p className="text-slate-700 font-semibold text-sm">
                          GMP data is temporarily unavailable
                        </p>
                        <p className="text-slate-400 text-xs">{error}</p>
                        <button
                          onClick={handleRefresh}
                          className="px-4 py-1.5 bg-[#008080] text-white text-xs font-semibold rounded-lg hover:bg-[#006666] transition-colors"
                        >
                          Retry Loading GMP
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : pageItems.length === 0 ? (
                  /* Empty State */
                  <tr>
                    <td colSpan={COLUMN_COUNT} className="py-12 text-center">
                      <div className="max-w-md mx-auto space-y-2 text-slate-500 px-4">
                        <Activity className="w-8 h-8 mx-auto text-slate-300" />
                        <p className="font-semibold text-slate-700 text-sm">
                          {items.length === 0
                            ? 'No IPOs are being tracked in the grey market right now'
                            : 'No IPOs match your filters'}
                        </p>
                        {items.length > 0 && (
                          <button
                            onClick={clearFilters}
                            className="mt-2 text-xs text-[#008080] font-semibold hover:underline"
                          >
                            Clear All Filters
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  /* Data Rows */
                  pageItems.map((row) => (
                    <tr key={row.key} className="hover:bg-teal-50/30 transition-colors">
                      {/* Company */}
                      <td className="py-3.5 px-4 sm:px-6">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-teal-50 text-[#008080] font-bold text-xs flex items-center justify-center border border-teal-100/80 flex-shrink-0">
                            {row.companyName.charAt(0)}
                          </div>
                          <div className="min-w-0">
                            {row.slug ? (
                              <Link
                                to={`/ipo-gmp/${row.slug}`}
                                className="block font-bold text-slate-900 truncate hover:text-[#008080] transition-colors"
                                title={row.companyName}
                              >
                                {row.companyName}
                              </Link>
                            ) : (
                              <div className="font-bold text-slate-900 truncate" title={row.companyName}>
                                {row.companyName}
                              </div>
                            )}
                            <div className="flex items-center gap-1.5 mt-0.5">
                              {row.board && (
                                <span
                                  className={`inline-flex items-center px-1.5 py-px rounded text-[10px] font-bold border ${
                                    row.isSme
                                      ? 'bg-amber-50 text-amber-700 border-amber-200'
                                      : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                  }`}
                                >
                                  {row.board}
                                </span>
                              )}
                              {row.exchange && (
                                <span className="text-[11px] text-slate-400">{row.exchange}</span>
                              )}
                              {row.rating && (
                                <span className="text-[11px]" title="Rating">
                                  {row.rating}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Status */}
                      <td className="py-3.5 px-4 text-center whitespace-nowrap">
                        {row.status ? (
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold border ${statusBadgeClass(row.statusKey)}`}
                          >
                            {row.status}
                          </span>
                        ) : (
                          <Placeholder />
                        )}
                      </td>

                      {/* GMP */}
                      <td className="py-3.5 px-4">{renderGmpCell(row)}</td>

                      {/* Estimated Listing */}
                      <td className="py-3.5 px-4 whitespace-nowrap font-semibold text-slate-800">
                        {row.estListingDisplay || <Placeholder />}
                      </td>

                      {/* Price */}
                      <td className="py-3.5 px-4 whitespace-nowrap text-slate-700">
                        {row.priceDisplay || <Placeholder>TBA</Placeholder>}
                      </td>

                      {/* Issue Size */}
                      <td className="py-3.5 px-4 whitespace-nowrap text-slate-700 hidden md:table-cell">
                        {row.issueSize || <Placeholder />}
                      </td>

                      {/* Lot Size */}
                      <td className="py-3.5 px-4 whitespace-nowrap text-slate-700 hidden lg:table-cell">
                        {row.lotSize || <Placeholder />}
                      </td>

                      {/* Subscription */}
                      <td className="py-3.5 px-4 whitespace-nowrap text-slate-700 hidden lg:table-cell">
                        {row.subscription || <Placeholder />}
                      </td>

                      {/* Open – Close */}
                      <td className="py-3.5 px-4 whitespace-nowrap">
                        {row.openDate || row.closeDate ? (
                          <div className="flex items-center gap-1.5 text-slate-700 font-medium text-xs">
                            <CalendarDays className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                            <span>
                              {row.openDate || '—'} – {row.closeDate || '—'}
                            </span>
                          </div>
                        ) : (
                          <Placeholder>TBA</Placeholder>
                        )}
                      </td>

                      {/* Allotment */}
                      <td className="py-3.5 px-4 whitespace-nowrap text-xs text-slate-700 hidden xl:table-cell">
                        {row.boaDate || <Placeholder />}
                      </td>

                      {/* Listing */}
                      <td className="py-3.5 px-4 sm:px-6 whitespace-nowrap text-xs">
                        <div className="font-semibold text-slate-700">
                          {row.listingDate || <Placeholder>TBA</Placeholder>}
                        </div>
                        {row.updatedOn && (
                          <div className="text-[10px] text-slate-400" title="Last updated at source">
                            Upd. {row.updatedOn}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Footer: Refresh Meta & Pagination */}
          <div className="bg-slate-50/80 px-4 sm:px-6 py-4 border-t border-slate-200/80 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="text-[11px] text-slate-500 text-center sm:text-left">
              {hasItems && (
                <>
                  Showing {pageItems.length} of {filteredItems.length}
                  {refreshedLabel && <> · Refreshed {refreshedLabel}</>}
                  {sourceLabel && <> · Source: {sourceLabel}</>}
                </>
              )}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center gap-1.5 sm:gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage <= 1}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-2xs"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                  <span>Previous</span>
                </button>

                <div className="flex items-center gap-1">{renderPaginationNumbers()}</div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-2xs"
                >
                  <span>Next</span>
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Disclaimer */}
        <p className="flex items-start gap-1.5 mt-4 text-[11px] text-slate-400 leading-relaxed">
          <Info className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
          <span>
            GMP is an unofficial, unregulated indicator from grey-market quotes and can change sharply. Estimated listing = upper price band + GMP. It is not a guarantee of listing price or investment advice.
          </span>
        </p>
      </div>
    </section>
  );
};

export default GmpSection;
